import { type Event, EventType } from '../types';

import { MAX_COMMENT_IDS_PER_READ, markCommentNotificationsAsRead } from './api';

/**
 * EventService buffers client-side interaction events and flushes them to the server.
 *
 * The server does not accept arbitrary events from clients. The only interaction the UI
 * reports is COMMENT_READ, which is sent to the dedicated
 * POST /api/notifications/comments/read endpoint. Other event types are dropped.
 */
export class EventService {
  private eventQueue: Event[] = [];
  private isSending = false;
  private flushInterval: number | null = null;
  private maxQueueSize = 10;
  private flushIntervalMs = 10000; // 10 seconds

  constructor() {
    // Set up periodic flush
    this.flushInterval = window.setInterval(() => this.flush(), this.flushIntervalMs);

    // Set up flush on window unload/tab close
    window.addEventListener('beforeunload', () => this.flush(true));

    // Set up flush on visibility change (tab switch)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush(true);
      }
    });
  }

  /**
   * Queue an event to be sent to the server
   */
  public queueEvent<T extends EventType>(event: Omit<Event<T>, 'queuedAt'>): void {
    this.eventQueue.push({
      ...event,
      queuedAt: new Date().toISOString(),
    });

    // If queue reaches a certain size, flush immediately
    if (this.eventQueue.length >= this.maxQueueSize) {
      this.flush();
    }
  }

  /**
   * Send an event right away by queueing it and flushing immediately
   */
  public async publishEvent<T extends EventType>(event: Omit<Event<T>, 'queuedAt'>): Promise<boolean> {
    this.queueEvent(event);
    return this.flush(true);
  }

  /**
   * Flush the event queue to the server
   */
  // `_immediate` is kept for API compatibility with existing callers.
  public async flush(_immediate = false): Promise<boolean> {
    if (this.isSending || this.eventQueue.length === 0) {
      return true;
    }

    this.isSending = true;

    try {
      const eventsToSend = [...this.eventQueue];
      this.eventQueue = [];

      const commentIds = new Set<string>();
      for (const event of eventsToSend) {
        if (event.type === EventType.COMMENT_READ) {
          const { commentId } = event.payload as { commentId?: string };
          if (commentId) {
            commentIds.add(commentId);
          }
        } else {
          console.warn(`Dropping unsupported client event type: ${event.type}`);
        }
      }

      const ids = [...commentIds];
      for (let i = 0; i < ids.length; i += MAX_COMMENT_IDS_PER_READ) {
        await markCommentNotificationsAsRead(ids.slice(i, i + MAX_COMMENT_IDS_PER_READ));
      }

      return true;
    } catch (error) {
      console.error('Failed to send events:', error);
      return false;
    } finally {
      this.isSending = false;
    }
  }

  /**
   * Clean up resources used by the event service
   */
  public dispose(): void {
    if (this.flushInterval !== null) {
      window.clearInterval(this.flushInterval);
    }

    window.removeEventListener('beforeunload', () => this.flush(true));
    document.removeEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush(true);
      }
    });

    // Final flush
    this.flush(true);
  }
}

// Create and export a singleton instance
export const eventService = new EventService();
